import React, { useState } from "react";
import {
  Box,
  Typography,
  MenuItem,
  Select,
  Chip,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import IOSSwitch from "@components/common/Switch";
import {
  type TimeSeriesUnit,
  type GuardrailRollingOption,
  type TimeSeriesViewerOptions,
  type TimeSeriesViewerUnitConfig,
  GUARDRAIL_ROLLING_OPTION_LABELS,
  sortGuardrailRollingOptions,
} from "@models/Dataset";
import { TabTimeAlignmentCallout } from "./TabTimeAlignmentCallout";
import { TabTimeBreakWarning } from "./TabTimeBreakWarning";
import { useTabTimeBreak } from "@hooks/use-tab-time-alignment";
import type { SharedTimeConfig } from "@utils/tab-time-controls";

const UNIT_LABELS: Record<TimeSeriesUnit, string> = {
  week: "Week",
  month: "Month",
  quarter: "Quarter",
  semester: "Semester",
};

interface UnitGuardrailInfo {
  enabled: boolean;
  allowedRollingAverages: GuardrailRollingOption[];
  defaultRollingAverage: GuardrailRollingOption;
}

interface TimeSeriesViewerOptionsSectionProps {
  viewerOptions: TimeSeriesViewerOptions;
  chartTimeUnit: TimeSeriesUnit;
  chartRollingWindowSize: number;
  tsGuardrailUnits: Partial<Record<TimeSeriesUnit, UnitGuardrailInfo>>;
  isDisabled: boolean;
  onViewerOptionsChange: (options: TimeSeriesViewerOptions) => void;
  /** Dataset of the chart being configured — drives the tab-time alignment callout. */
  datasetId?: string | null;
  /** Revert saved time unit + rolling average + viewer contract to a shared config. */
  onRestoreSharedTime?: (config: SharedTimeConfig) => void;
}

// ─── Per-unit RA row ──────────────────────────────────────────────────────────

interface UnitSectionProps {
  unit: TimeSeriesUnit;
  isChartUnit: boolean;
  chartRA: GuardrailRollingOption;
  allowedRAs: GuardrailRollingOption[];
  checkedRAs: GuardrailRollingOption[];
  defaultRA?: GuardrailRollingOption;
  isDisabled: boolean;
  onRAToggle: (ra: GuardrailRollingOption, checked: boolean) => void;
  onSetDefault?: (ra: GuardrailRollingOption) => void;
  onRemove?: () => void;
}

const UnitSection: React.FC<UnitSectionProps> = ({
  unit,
  isChartUnit,
  chartRA,
  allowedRAs,
  checkedRAs,
  defaultRA,
  isDisabled,
  onRAToggle,
  onSetDefault,
  onRemove,
}) => {
  const [expanded, setExpanded] = useState(true);
  const sortedAllowedRAs = sortGuardrailRollingOptions(allowedRAs);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        onClick={() => setExpanded((p) => !p)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 1,
          bgcolor: "grey.50",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {expanded ? (
            <ExpandLessIcon fontSize="small" sx={{ color: "text.secondary" }} />
          ) : (
            <ExpandMoreIcon fontSize="small" sx={{ color: "text.secondary" }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {UNIT_LABELS[unit]}
            {isChartUnit ? " (chart's unit)" : ""}
          </Typography>
          {isChartUnit && (
            <Chip label="Saved config" size="small" sx={{ fontSize: 10, height: 18 }} />
          )}
        </Box>
        {!isChartUnit && onRemove && (
          <IconButton
            size="small"
            disabled={isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, py: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            {isChartUnit
              ? "Viewer-selectable rolling average options for this unit:"
              : "Rolling average options when viewer selects this unit:"}
          </Typography>

          {sortedAllowedRAs.map((ra) => {
            const isChartRA = isChartUnit && ra === chartRA;
            const isChecked = isChartRA || checkedRAs.includes(ra);
            const isDefault = isChartUnit ? ra === chartRA : ra === defaultRA;

            return (
              <Box
                key={ra}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isDisabled || isChartRA}
                    onChange={(e) => onRAToggle(ra, e.target.checked)}
                    style={{ cursor: isChartRA ? "default" : "pointer" }}
                  />
                  <Typography
                    variant="body2"
                    color={isChartRA ? "text.secondary" : "text.primary"}
                  >
                    {GUARDRAIL_ROLLING_OPTION_LABELS[ra]}
                    {isChartRA && " ↑"}
                  </Typography>
                </Box>

                {!isChartUnit && isChecked && onSetDefault && (
                  isDefault ? (
                    <Chip
                      label="Default"
                      size="small"
                      color="primary"
                      sx={{ fontSize: 10, height: 18 }}
                    />
                  ) : (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => onSetDefault(ra)}
                    >
                      Set default
                    </Typography>
                  )
                )}
                {isChartUnit && isDefault && (
                  <Chip
                    label="Saved config"
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 10, height: 18 }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Main section ─────────────────────────────────────────────────────────────

const TimeSeriesViewerOptionsSection: React.FC<
  TimeSeriesViewerOptionsSectionProps
> = ({
  viewerOptions,
  chartTimeUnit,
  chartRollingWindowSize,
  tsGuardrailUnits,
  isDisabled,
  onViewerOptionsChange,
  datasetId = null,
  onRestoreSharedTime,
}) => {
  const [sectionCollapsed, setSectionCollapsed] = useState(false);

  const chartRA = chartRollingWindowSize as GuardrailRollingOption;

  // Single break computation shared by the warning and the alignment callout.
  const brokenAlignment = useTabTimeBreak(
    "trend",
    datasetId,
    chartTimeUnit,
    chartRA,
    viewerOptions,
  );

  const chartUnitGuardrail = tsGuardrailUnits[chartTimeUnit];
  const chartUnitAllowedRAs: GuardrailRollingOption[] =
    chartUnitGuardrail?.allowedRollingAverages ?? [];

  const addedAdditionalUnits = Object.keys(
    viewerOptions.additionalUnits,
  ) as TimeSeriesUnit[];

  const remainingUnits = (
    Object.entries(tsGuardrailUnits) as [TimeSeriesUnit, UnitGuardrailInfo][]
  )
    .filter(
      ([unit, g]) =>
        unit !== chartTimeUnit &&
        g.enabled &&
        !addedAdditionalUnits.includes(unit),
    )
    .map(([unit]) => unit);

  const handleToggleEnabled = () => {
    onViewerOptionsChange({ ...viewerOptions, enabled: !viewerOptions.enabled });
  };

  const handleChartUnitRAToggle = (
    ra: GuardrailRollingOption,
    checked: boolean,
  ) => {
    const current = viewerOptions.chartUnitAdditionalRollingAverages;
    const updated = checked
      ? ([...current, ra] as GuardrailRollingOption[])
      : current.filter((r) => r !== ra);
    onViewerOptionsChange({
      ...viewerOptions,
      chartUnitAdditionalRollingAverages: updated,
    });
  };

  const handleAddUnit = (unit: TimeSeriesUnit) => {
    const g = tsGuardrailUnits[unit];
    if (!g) return;
    const defaultRA = g.defaultRollingAverage;
    const newConfig: TimeSeriesViewerUnitConfig = {
      allowedRollingAverages: [defaultRA],
      defaultRollingAverage: defaultRA,
    };
    onViewerOptionsChange({
      ...viewerOptions,
      additionalUnits: { ...viewerOptions.additionalUnits, [unit]: newConfig },
    });
  };

  const handleRemoveUnit = (unit: TimeSeriesUnit) => {
    const { [unit]: _removed, ...remaining } = viewerOptions.additionalUnits;
    onViewerOptionsChange({ ...viewerOptions, additionalUnits: remaining });
  };

  const handleAdditionalUnitRAToggle = (
    unit: TimeSeriesUnit,
    ra: GuardrailRollingOption,
    checked: boolean,
  ) => {
    const config = viewerOptions.additionalUnits[unit];
    if (!config) return;
    const current = config.allowedRollingAverages;
    const updated = checked
      ? ([...current, ra] as GuardrailRollingOption[])
      : current.filter((r) => r !== ra);
    const newDefault = updated.includes(config.defaultRollingAverage)
      ? config.defaultRollingAverage
      : (updated[0] ?? ra);
    onViewerOptionsChange({
      ...viewerOptions,
      additionalUnits: {
        ...viewerOptions.additionalUnits,
        [unit]: { allowedRollingAverages: updated, defaultRollingAverage: newDefault },
      },
    });
  };

  const handleSetDefault = (unit: TimeSeriesUnit, ra: GuardrailRollingOption) => {
    const config = viewerOptions.additionalUnits[unit];
    if (!config) return;
    onViewerOptionsChange({
      ...viewerOptions,
      additionalUnits: {
        ...viewerOptions.additionalUnits,
        [unit]: { ...config, defaultRollingAverage: ra },
      },
    });
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          mb: sectionCollapsed ? 0 : 2,
        }}
        onClick={() => setSectionCollapsed((p) => !p)}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Viewer Time Options
        </Typography>
        {sectionCollapsed ? (
          <ExpandMoreIcon fontSize="small" sx={{ color: "text.secondary" }} />
        ) : (
          <ExpandLessIcon fontSize="small" sx={{ color: "text.secondary" }} />
        )}
      </Box>

      <Collapse in={!sectionCollapsed}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {onRestoreSharedTime && (
            <TabTimeBreakWarning
              broken={brokenAlignment}
              onRestore={onRestoreSharedTime}
            />
          )}
          <TabTimeAlignmentCallout
            kind="trend"
            datasetId={datasetId}
            savedSetting={chartTimeUnit}
            savedRA={chartRA}
            currentViewerOptions={viewerOptions}
            breaksAlignment={brokenAlignment !== null}
            onApply={(opts) =>
              onViewerOptionsChange(opts as TimeSeriesViewerOptions)
            }
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2">Enable viewer controls</Typography>
            <IOSSwitch
              checked={viewerOptions.enabled}
              onChange={handleToggleEnabled}
              disabled={isDisabled}
            />
          </Box>

          {viewerOptions.enabled && (
            <>
              <Typography variant="caption" color="text.secondary">
                Configure which time units and rolling average options viewers
                can select. The chart&apos;s saved config is always included.
              </Typography>

              <UnitSection
                unit={chartTimeUnit}
                isChartUnit
                chartRA={chartRA}
                allowedRAs={chartUnitAllowedRAs}
                checkedRAs={viewerOptions.chartUnitAdditionalRollingAverages}
                isDisabled={isDisabled}
                onRAToggle={handleChartUnitRAToggle}
              />

              {addedAdditionalUnits.map((unit) => {
                const config = viewerOptions.additionalUnits[unit];
                const g = tsGuardrailUnits[unit];
                if (!config || !g) return null;
                return (
                  <UnitSection
                    key={unit}
                    unit={unit}
                    isChartUnit={false}
                    chartRA={chartRA}
                    allowedRAs={g.allowedRollingAverages}
                    checkedRAs={config.allowedRollingAverages}
                    defaultRA={config.defaultRollingAverage}
                    isDisabled={isDisabled}
                    onRAToggle={(ra, checked) =>
                      handleAdditionalUnitRAToggle(unit, ra, checked)
                    }
                    onSetDefault={(ra) => handleSetDefault(unit, ra)}
                    onRemove={() => handleRemoveUnit(unit)}
                  />
                );
              })}

              {remainingUnits.length > 0 && (
                <Select
                  displayEmpty
                  value=""
                  size="small"
                  disabled={isDisabled}
                  onChange={(e) => handleAddUnit(e.target.value as TimeSeriesUnit)}
                  renderValue={() => (
                    <Typography variant="body2" color="text.secondary">
                      + Add time unit
                    </Typography>
                  )}
                >
                  {remainingUnits.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {UNIT_LABELS[unit]}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default TimeSeriesViewerOptionsSection;
